import styled from "@emotion/styled"
import { Box, Text, textStyles, theme } from "@pallet-hq/hegel"
import { ReactComponent as PalletLogo } from "components/icons/logos/pallet/logomark.svg"
import NextLink from "components/Link/NextLink"
import BaseNavbar from "components/Navbar"
import AccountControl from "components/Navbar/AccountControl"
import { usePalletRouter } from "utils/hooks/usePalletRouter"
import { useQuery } from "@apollo/client"
import { PARTNERSHIP_TIER_JOB_SLOTS_QUERY } from "page-components/hire/gql"
import { PartnershipTierJobSlotsQuery } from "__generated__/PartnershipTierJobSlotsQuery"

type NavbarCenterProps = {
    step: "package" | "setup" | "job" | "checkout"
}

const NavbarCenterStepSwitch = ({
    step,
    showJobPostStep = true,
}: {
    step: "package" | "setup" | "job" | "checkout"
    showJobPostStep?: boolean
}) => {
    switch (step) {
        case "package":
            return (
                <>
                    <HighlightedText>Choose a package</HighlightedText>
                    <BaseText>
                        / Set up your account{" "}
                        {showJobPostStep && "/ Add a job post "}/ Purchase
                    </BaseText>
                </>
            )
        case "setup":
            return (
                <>
                    <BaseText>Choose a package /</BaseText>
                    <HighlightedText>Set up your account</HighlightedText>
                    <BaseText>
                        {showJobPostStep && "/ Add a job post "}/ Purchase
                    </BaseText>
                </>
            )
        case "job":
            return (
                <>
                    <BaseText>
                        Choose a package / Set up your account /
                    </BaseText>
                    <HighlightedText>Add a job post</HighlightedText>
                    <BaseText>/ Purchase</BaseText>
                </>
            )
        case "checkout":
            return (
                <>
                    <BaseText>
                        Choose a package / Set up your account{" "}
                        {showJobPostStep && "/ Add a job post "}/
                    </BaseText>
                    <HighlightedText>Purchase</HighlightedText>
                </>
            )
    }
}

export const HireNavbarCenter = ({ step }: NavbarCenterProps) => {
    const router = usePalletRouter()
    const { item } = router.query
    const { data } = useQuery<PartnershipTierJobSlotsQuery>(
        PARTNERSHIP_TIER_JOB_SLOTS_QUERY,
        {
            variables: { itemUuid: item },
            skip: !router.query.item,
        }
    )

    const showJobPostStep = data?.partnershipTier?.activeJobPostLimit !== 0

    return (
        <Box
            display="flex"
            justifyContent="center"
            style={{ gap: theme.space.xsmall, width: "550px" }}
        >
            <NavbarCenterStepSwitch
                step={step}
                showJobPostStep={showJobPostStep}
            />
        </Box>
    )
}

const HireNavbar = ({ step }: NavbarCenterProps) => {
    return (
        <BaseNavbar
            left={
                <NextLink href="/hire">
                    <PalletLogo
                        width={107}
                        height={24}
                        style={{ cursor: "pointer" }}
                    />
                </NextLink>
            }
            center={<HireNavbarCenter step={step} />}
            right={<AccountControl />}
        />
    )
}

export default HireNavbar

const BaseText = styled(Text)`
    ${textStyles.uiLarge400};
    color: ${theme.colors.gray600};
`

const HighlightedText = styled(Text)`
    ${textStyles.uiLarge500};
    color: ${theme.colors.gray900};
`
